import type { DsdEditorParagraphSegment } from "@/apis/dsd-editor/route";
import type { DsdEditorParsedItem } from "@/utils/dsd-editor/document-items";
import { type RefObject, useMemo } from "react";
import {
  type DsdReviewEvidence,
  DsdReviewEvidencePresence,
} from "./evidence-popover";
import {
  useDsdReviewEvidenceIndex,
  useDsdReviewEvidenceStore,
} from "./evidence-store";

/**
 * 근거 창이 후보에게서 읽는 것 — 조립이 패널의 카드 모델에서 이 모양으로 만들어 넘긴다
 * (evidence → panel 의존을 두지 않기 위한 좁은 타입). onReview 는 검토 쌍이 있는 후보에만 있고, 패널 카드의 원형·사유와
 * 같은 모달을 연다 — 판단이 저장된 후보(summary.judgement)면 저장된 판단으로 채운 수정 모달
 */
export type DsdReviewEvidenceCandidate = Pick<
  DsdReviewEvidence,
  "occurrence" | "tone" | "hasEvidence" | "onReview" | "summary"
>;

/** store 의 후보 index 를 선택 셀의 후보 목록에 맞춰 근거 창 내용으로 만든다. ←/→ 에 다시 렌더되는 건 여기 아래뿐 */
export const DsdReviewEvidenceHost = ({
  selectedCellId,
  items,
  ...rest
}: {
  selectedCellId: string | null;
  /** 선택 셀의 후보 (패널과 같은 순서 — 1/N 이 패널 순서를 따른다) */
  items: DsdReviewEvidenceCandidate[];
  scrollContainerRef: RefObject<HTMLDivElement>;
  itemsBySection: ReadonlyMap<string, DsdEditorParsedItem[]>;
  matchedParagraphSegments?: ReadonlyMap<string, DsdEditorParagraphSegment[]>;
}) => {
  const index = useDsdReviewEvidenceIndex(selectedCellId);
  const setTarget = useDsdReviewEvidenceStore((state) => state.setTarget);
  const item = index === null ? null : (items[index] ?? null);
  const total = items.length;
  // 참조가 내용과 함께만 바뀌어야 한다 — 닫힘 애니메이션 동안 마지막 내용을 붙잡는 Presence 가 참조로 변화를 본다
  const evidence = useMemo<DsdReviewEvidence | null>(
    () =>
      selectedCellId && index !== null && item
        ? {
            anchorCellId: selectedCellId,
            occurrence: item.occurrence,
            tone: item.tone,
            index,
            total,
            hasEvidence: item.hasEvidence,
            summary: item.summary,
            onReview: item.onReview,
            // 순환: 첫 후보에서 이전 → 마지막, 마지막에서 다음 → 첫
            onPrev: () =>
              setTarget({
                cellId: selectedCellId,
                index: (index - 1 + total) % total,
              }),
            onNext: () =>
              setTarget({ cellId: selectedCellId, index: (index + 1) % total }),
            onClose: () => setTarget(null),
          }
        : null,
    [selectedCellId, index, item, total, setTarget],
  );
  return <DsdReviewEvidencePresence evidence={evidence} {...rest} />;
};
